import { useState, useEffect, useRef } from 'react';
import { useOrderStore, useResourceStore } from '@/store';
import { Order } from '@/types';
import { toast } from '@/components/UI/Toast';

interface OrderFormProps {
  onClose: () => void;
  orderToEdit?: Order | null;
}

interface FormErrors {
  productId?: string;
  customer?: string;
  batchNo?: string;
  plannedDeliveryDate?: string;
  totalQuantity?: string;
}

export function OrderForm({ onClose, orderToEdit }: OrderFormProps) {
  const addOrder = useOrderStore((state) => state.addOrder);
  const updateOrder = useOrderStore((state) => state.updateOrder);
  const products = useResourceStore((state) => state.products);
  const isEditMode = !!orderToEdit;

  // Ref for first input (auto-focus)
  const firstInputRef = useRef<HTMLSelectElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ESC 鍵關閉對話框
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Auto-focus first input when dialog opens
    firstInputRef.current?.focus();

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const [formData, setFormData] = useState({
    productId: '',
    productName: '',
    totalQuantity: 100,
    customer: '',
    batchNo: '',
    orderDate: new Date().toISOString().slice(0, 10),
    plannedDeliveryDate: '',
    progress: 0,
    status: '待處理' as Order['status'],
    note: '',
  });

  // 當 orderToEdit 改變時，更新表單資料
  useEffect(() => {
    if (orderToEdit) {
      setFormData({
        productId: orderToEdit.productId,
        productName: orderToEdit.productName,
        totalQuantity: orderToEdit.totalQuantity,
        customer: orderToEdit.customer,
        batchNo: orderToEdit.batchNo,
        orderDate: orderToEdit.orderDate.slice(0, 10),
        plannedDeliveryDate: orderToEdit.plannedDeliveryDate.slice(0, 10),
        progress: orderToEdit.progress,
        status: orderToEdit.status,
        note: orderToEdit.note || '',
      });
    }
  }, [orderToEdit]);

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // 即時驗證函數
  const validateField = (name: string, value: any) => {
    switch (name) {
      case 'productId':
        if (!value) return '請選擇產品';
        return '';
      case 'customer':
        if (!value.trim()) return '請輸入客戶名稱';
        if (value.trim().length < 2) return '客戶名稱至少需要 2 個字元';
        return '';
      case 'batchNo':
        if (!value.trim()) return '請輸入批號';
        return '';
      case 'plannedDeliveryDate':
        if (!value) return '請選擇預計交期';
        const selectedDate = new Date(value);
        const orderDate = new Date(formData.orderDate);
        if (selectedDate <= orderDate) return '交期必須晚於接單日期';
        return '';
      case 'totalQuantity':
        if (!value || value <= 0) return '數量必須大於 0';
        if (value > 1000000) return '數量不能超過 1,000,000';
        return '';
      default:
        return '';
    }
  };

  // 處理欄位變更並即時驗證
  const handleChange = (name: string, value: any) => {
    setFormData({ ...formData, [name]: value });

    // 如果該欄位已被觸碰過，即時驗證
    if (touched.has(name)) {
      const error = validateField(name, value);
      const newErrors = { ...errors };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      setErrors(newErrors);
    }
  };

  // 處理欄位失焦
  const handleBlur = (name: string) => {
    setTouched(new Set([...touched, name]));
    const error = validateField(name, formData[name as keyof typeof formData]);
    const newErrors = { ...errors };
    if (error) {
      newErrors[name] = error;
    } else {
      delete newErrors[name];
    }
    setErrors(newErrors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 驗證所有欄位
    const newErrors: FormErrors = {};
    (Object.keys(formData) as Array<keyof typeof formData>).forEach((key) => {
      if (key !== 'productName' && key !== 'progress' && key !== 'status' && key !== 'note') {
        const error = validateField(key, formData[key]);
        if (error) newErrors[key] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched(new Set(Object.keys(newErrors)));
      toast.error('請修正表單錯誤', '有些欄位需要修正');
      return;
    }

    setIsSubmitting(true);

    // 模擬 API 延遲以提供視覺反饋
    await new Promise(resolve => setTimeout(resolve, 500));

    try {
      if (isEditMode && orderToEdit) {
        // 更新現有訂單
        updateOrder(orderToEdit.id, {
          ...formData,
          plannedDeliveryDate: new Date(formData.plannedDeliveryDate).toISOString(),
          orderDate: new Date(formData.orderDate).toISOString(),
        });

        // 成功提示
        toast.success(
          '訂單更新成功',
          `訂單 ${orderToEdit.id} 已成功更新`,
          3000
        );
      } else {
        // 建立新訂單
        const newOrder: Order = {
          id: `WO-${Date.now()}`,
          ...formData,
          plannedDeliveryDate: new Date(formData.plannedDeliveryDate).toISOString(),
          orderDate: new Date(formData.orderDate).toISOString(),
          actualDeliveryDate: null,
          priority: 999,
          mainWorkOrders: [],
        };

        addOrder(newOrder);

        // 成功提示
        toast.success(
          '訂單建立成功',
          `訂單 ${newOrder.id} 已成功建立`,
          3000
        );
      }

      setIsSubmitting(false);
      onClose();
    } catch (error) {
      console.error('保存訂單時發生錯誤:', error);
      setIsSubmitting(false);
      toast.error(
        '保存失敗',
        error instanceof Error ? error.message : '發生未預期的錯誤，請稍後再試',
        5000
      );
    }
  };

  const handleProductChange = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setFormData({
        ...formData,
        productId,
        productName: product.name,
      });
      // 即時驗證（與 handleChange 邏輯一致）
      if (touched.has('productId')) {
        const error = validateField('productId', productId);
        const newErrors = { ...errors };
        if (error) {
          newErrors.productId = error;
        } else {
          delete newErrors.productId;
        }
        setErrors(newErrors);
      }
    }
  };

  // 輔入框樣式（含錯誤狀態）
  const getInputClass = (fieldName: string) => {
    const baseClass = "w-full px-4 py-2.5 bg-white border-2 rounded-xl text-sm font-medium transition-all focus:ring-2 focus:ring-offset-0 focus:outline-none";
    const errorClass = errors[fieldName]
      ? "border-red-300 focus:ring-red-400 focus:border-red-400"
      : "border-slate-200 focus:ring-blue-400 focus:border-blue-300";
    return `${baseClass} ${errorClass}`;
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
      <div className="bg-gradient-to-br from-white to-slate-50 border-2 border-slate-200 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/30">
            <span className="text-white text-lg">{isEditMode ? '✏️' : '📦'}</span>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">{isEditMode ? '編輯訂單' : '新增訂單'}</h2>
            <p className="text-sm text-slate-500">{isEditMode ? '修改訂單資訊' : '建立新的生產訂單'}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* 產品選擇 */}
          <div>
            <label htmlFor="productId" className="block text-sm font-medium text-slate-700 mb-1">
              產品 <span className="text-red-500">*</span>
            </label>
            <select
              id="productId"
              ref={firstInputRef}
              value={formData.productId}
              onChange={(e) => handleProductChange(e.target.value)}
              onBlur={() => handleBlur('productId')}
              className={getInputClass('productId')}
              required
              aria-required="true"
              aria-invalid={errors.productId && touched.has('productId') ? 'true' : 'false'}
              aria-describedby={errors.productId && touched.has('productId') ? 'productId-error' : undefined}
            >
              <option value="">請選擇產品</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.id} - {product.name} ({product.specification})
                </option>
              ))}
            </select>
            {errors.productId && touched.has('productId') && (
              <p id="productId-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.productId}</span>
              </p>
            )}
          </div>

          {/* 客戶名稱 */}
          <div>
            <label htmlFor="customer" className="block text-sm font-medium text-slate-700 mb-1">
              客戶名稱 <span className="text-red-500">*</span>
            </label>
            <input
              id="customer"
              type="text"
              value={formData.customer}
              onChange={(e) => handleChange('customer', e.target.value)}
              onBlur={() => handleBlur('customer')}
              className={getInputClass('customer')}
              placeholder="輸入客戶名稱"
              required
              aria-required="true"
              aria-invalid={errors.customer && touched.has('customer') ? 'true' : 'false'}
              aria-describedby={errors.customer && touched.has('customer') ? 'customer-error' : 'customer-hint'}
            />
            <p id="customer-hint" className="mt-1 text-xs text-slate-500">
              請輸入完整的客戶公司或個人名稱（至少 2 個字元）
            </p>
            {errors.customer && touched.has('customer') && (
              <p id="customer-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.customer}</span>
              </p>
            )}
          </div>

          {/* 批號 */}
          <div>
            <label htmlFor="batchNo" className="block text-sm font-medium text-slate-700 mb-1">
              批號 <span className="text-red-500">*</span>
            </label>
            <input
              id="batchNo"
              type="text"
              value={formData.batchNo}
              onChange={(e) => handleChange('batchNo', e.target.value)}
              onBlur={() => handleBlur('batchNo')}
              className={getInputClass('batchNo')}
              placeholder="輸入批號"
              required
              aria-required="true"
              aria-invalid={errors.batchNo && touched.has('batchNo') ? 'true' : 'false'}
              aria-describedby={errors.batchNo && touched.has('batchNo') ? 'batchNo-error' : 'batchNo-hint'}
            />
            <p id="batchNo-hint" className="mt-1 text-xs text-slate-500">
              此批號將用於追蹤生產批次，例如：BATCH-2026-001
            </p>
            {errors.batchNo && touched.has('batchNo') && (
              <p id="batchNo-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.batchNo}</span>
              </p>
            )}
          </div>

          {/* 數量 */}
          <div>
            <label htmlFor="totalQuantity" className="block text-sm font-medium text-slate-700 mb-1">
              數量 <span className="text-red-500">*</span>
            </label>
            <input
              id="totalQuantity"
              type="number"
              min="1"
              max="1000000"
              value={formData.totalQuantity}
              onChange={(e) => handleChange('totalQuantity', Number(e.target.value))}
              onBlur={() => handleBlur('totalQuantity')}
              className={getInputClass('totalQuantity')}
              required
              aria-required="true"
              aria-invalid={errors.totalQuantity && touched.has('totalQuantity') ? 'true' : 'false'}
              aria-describedby={errors.totalQuantity && touched.has('totalQuantity') ? 'totalQuantity-error' : 'totalQuantity-hint'}
            />
            <p id="totalQuantity-hint" className="mt-1 text-xs text-slate-500">
              訂單數量（範圍：1 - 1,000,000）
            </p>
            {errors.totalQuantity && touched.has('totalQuantity') && (
              <p id="totalQuantity-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.totalQuantity}</span>
              </p>
            )}
          </div>

          {/* 接單日期 */}
          <div>
            <label htmlFor="orderDate" className="block text-sm font-medium text-slate-700 mb-1">
              接單日期 <span className="text-red-500">*</span>
            </label>
            <input
              id="orderDate"
              type="date"
              value={formData.orderDate}
              onChange={(e) => setFormData({ ...formData, orderDate: e.target.value })}
              className={getInputClass('orderDate')}
              required
              aria-required="true"
            />
          </div>

          {/* 預計交期 */}
          <div>
            <label htmlFor="plannedDeliveryDate" className="block text-sm font-medium text-slate-700 mb-1">
              預計交期 <span className="text-red-500">*</span>
            </label>
            <input
              id="plannedDeliveryDate"
              type="date"
              value={formData.plannedDeliveryDate}
              onChange={(e) => handleChange('plannedDeliveryDate', e.target.value)}
              onBlur={() => handleBlur('plannedDeliveryDate')}
              className={getInputClass('plannedDeliveryDate')}
              min={formData.orderDate}
              required
              aria-required="true"
              aria-invalid={errors.plannedDeliveryDate && touched.has('plannedDeliveryDate') ? 'true' : 'false'}
              aria-describedby={errors.plannedDeliveryDate && touched.has('plannedDeliveryDate') ? 'plannedDeliveryDate-error' : 'plannedDeliveryDate-hint'}
            />
            <p id="plannedDeliveryDate-hint" className="mt-1 text-xs text-slate-500">
              交期必須晚於接單日期
            </p>
            {errors.plannedDeliveryDate && touched.has('plannedDeliveryDate') && (
              <p id="plannedDeliveryDate-error" className="mt-1.5 text-sm text-red-600 flex items-center gap-1" role="alert">
                <span aria-hidden="true">⚠️</span>
                <span>{errors.plannedDeliveryDate}</span>
              </p>
            )}
          </div>

          {/* 備註 */}
          <div>
            <label htmlFor="note" className="block text-sm font-medium text-slate-700 mb-1">備註</label>
            <textarea
              id="note"
              value={formData.note}
              onChange={(e) => setFormData({ ...formData, note: e.target.value })}
              className="w-full px-4 py-2.5 bg-white border-2 border-slate-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-400 focus:border-blue-300 transition-all focus:outline-none resize-none"
              rows={3}
              placeholder="輸入備註事項（選填）"
              aria-describedby="note-hint"
            />
            <p id="note-hint" className="mt-1 text-xs text-slate-500">
              選填欄位，可記錄特殊需求或注意事項
            </p>
          </div>

          {/* 按鈕 */}
          <div className="flex justify-end gap-3 pt-6 border-t-2 border-slate-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 rounded-xl hover:from-slate-200 hover:to-slate-300 transition-all text-sm font-semibold shadow-sm hover:shadow-md active:scale-95"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={Object.keys(errors).length > 0 || isSubmitting}
              className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl hover:from-blue-700 hover:to-blue-600 transition-all text-sm font-semibold shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  <span>處理中...</span>
                </>
              ) : (
                <>{isEditMode ? '💾 儲存變更' : '➕ 新增訂單'}</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
